// Description: Page to update the data of an existing template
// Updated: 22/08/2025

// React Components and DOM Elements
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Material UI Components
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";

// Components and functions from the project
import { GetVariables } from "../../api/aws/Listagens/GetVariables";
import ViewWhatsAppScreen from "../../components/WhatsAppScreen/ViewWhatsAppScreen";
import { isAuthenticated } from "../../authMiddleware";
import HeaderPage from "../../components/HeaderPage/HeaderPage";
import ThemeColors from "../../components/ThemeColors/ThemeColors";
import Footer from "../../components/Footer/Footer";
import Loading from "../../components/Loading/Loading";
import { TemplateUpdate } from "../../api/aws/Templates/UpdateTemplate";
import { GetAllTemplates } from "../../api/aws/Templates/GetAllTemplates";
import { notify } from "../../utils/utils";
import ModalR from "../../components/Modals/ModalR";
import use123zapStore from "../../context/123zapStore";
import TemplateInput from "../../components/Input/TemplateInput";
import themeColors from "../../components/ThemeColors/ThemeColors";
import StatusIcon from "../../components/StatusLabel/StatusIcon";
import DynamicButton from "../../components/DynamicButton/DynamicButton";
import TestTemplateModal from "../../components/Modals/TestTemplateModal";
import { useDropzone } from "react-dropzone";
import { TestMessage } from "../../api/aws/Templates/TestMessage";

function EditWhatsAppAPI() {
    // Authentication Context Functions
    const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
    const userInfo = use123zapStore((state) => state.userInfo);
    const setUserInfo = use123zapStore((state) => state.setUserInfo);
    const setAlreadyFetchTemplates = use123zapStore((state) => state.setAlreadyFetchTemplates);
    const Navigate = useNavigate();

    // Template Id State
    const [templateId, setTemplateId] = useState(
        localStorage.getItem("templateId") || null
    );

    // Form States default
    const [title, setTitle] = useState("");
    const [typeMessage, setTypeMessage] = useState("Atrasos");
    const [selected, setSelected] = useState("");
    const buttons = [
        "Atrasos",
        "Avisos",
        "Faturas",
        "Inadimplências",
        "Marketing",
        "Outros",
    ];

    const isSmallScreen = useMediaQuery("(max-width:1200px)");
    const [showPreview, setShowPreview] = useState(false);
    const [headerType, setHeaderType] = useState('none');
    const [header, setHeader] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [openTestModal, setOpenTestModal] = useState(false);
    const [disabledTestModal, setDisabledTestModal] = useState(false);
    const [wppNumber, setWppNumber] = useState("");
    const [variablesOfTemplate, setVariablesOfTemplate] = useState({});
    const [messageTemporary, setMessageTemporary] = useState("");
    const [prevMessageTemporary, setPrevMessageTemporary] = useState("");
    const [typeChannel, setTypeChannel] = useState("");

    // Loading Transition state
    const [showLoading, setShowLoading] = useState(true);

    // Component Props State
    const [viewWhatsAppScreenProps, setViewWhatsAppScreenProps] = useState({
        type: 'whatsapp',
        messages: {
            messages: [],
        },
    });

    // Effect to Check Authentication and Fetch User Info
    useEffect(() => {
        if (userAuthenticated && Object.keys(userInfo).length === 0) {
            const { decryptedData } = isAuthenticated();
            setUserInfo(decryptedData);
        }
    }, []);

    // Effect to Fetch Data from API
    useEffect(() => {
        if (userInfo["UUID"]) {
            fetchData();

            if (templateId === null) {
                Navigate("/modelos");
                return;
            }

            fetchTemplatesData();
        }
    }, [userInfo]);

    const fetchData = async () => {
        try {
            const translations = {
                Personal_information: "Informações Pessoais",
                Contact: "Contato",
                Address: "Endereço",
                Financial_References: "Referências Financeiras",
            };

            const resultJson = await GetVariables();
            const result = resultJson.data.reduce((acc, item) => {
                const category = translations[item.category] || item.category; // Traduz a categoria, ou usa o nome original caso não haja tradução
                if (!acc[category]) {
                    acc[category] = [];
                }

                // limita a 10 itens

                acc[category].push(item.variableName);

                return acc;
            }, {});

            //console.log(result, "Template Variables");

            setVariablesOfTemplate(result);
        } catch (error) {
            console.error("Error fetching data from API:", error);
        }
    };

    useEffect(() => {
        setViewWhatsAppScreenProps((prevProps) => ({
            ...prevProps,
            messages: {
                messages: [{ id: prevProps.messages.messages.length + 1, type: 'whatsapp', message: messageTemporary, fromMe: true, disabledRemove: true }],
            },
        }));

        if (messageTemporary !== prevMessageTemporary) {
            setDisabledTestModal(true)
        } else {
            setDisabledTestModal(false)
        };

    }, [messageTemporary]);

    function updateMessageType(object, setHeader, setHeaderType) {
        const correctedArray = object.messages.map((item) => {
            const isURL = String(item.message.content).startsWith('https://');
            const isBase64 = String(item.message.content).startsWith('data:image/');
            const isSMS = item.type === "sms";
            const isHeader = item.message.type === "header-img" || item.message.type === "header-txt"

            if (isURL || isBase64) {
                return {
                    id: item.id,
                    type: 'img',
                    message: item.message.content,
                    fromMe: item.fromMe
                };
            }

            if (isSMS) {
                return {
                    id: item.id,
                    type: 'sms',
                    message: item.message.content,
                    fromMe: item.fromMe
                };
            }

            if (isHeader) {
                setHeader(item.message.content)
                setHeaderType(item.message.type === "header-img" ? "image" : "text");
                return null;
            }

            if (item.type.includes("whatsapp") && !isHeader) {
                return {
                    id: item.id,
                    type: 'whatsapp',
                    message: item.message.content,
                    fromMe: item.fromMe,
                    disabledRemove: true
                };
            }
        });

        const data = {
            messages: correctedArray.filter((item) => item !== null && item !== undefined),
            type: "whatsapp"
        };

        return data;
    }

    // Function to get the template data
    const fetchTemplatesData = async () => {
        try {
            const resultJson = await GetAllTemplates();

            const items = resultJson.data
                .map((template) => {
                    return {
                        id: template.id,
                        title: template.title,
                        channel_type: template.channel_type,
                        status: template.status,
                        msg_type: template.msg_type,
                        type: template.type,
                        background: template.background,
                        messageJson: template.channel_type === "WhatsAppAPI" || template.channel_type === "SMS" ? updateMessageType(template.message_json, setHeader, setHeaderType) : template.message_json,
                    };
                })
                .filter((item) => item !== null);

            console.log(items, 'items')

            const findTemplate = items.find((item) => item.id === templateId);

            setTypeChannel(findTemplate.channel_type);
            setSelected(findTemplate.msg_type);

            setTitle(findTemplate.title);

            const findMessage = findTemplate.messageJson.messages.find(
                (message) => message.type === "whatsapp"
            );

            setMessageTemporary(findMessage.message);
            setPrevMessageTemporary(findMessage.message);

            setViewWhatsAppScreenProps((prevProps) => ({
                ...prevProps,
                messages: {
                    messages: findTemplate.messageJson.messages,
                },
            }));

        } catch (error) {
            console.error("Erro ao obter modelos:", error);
        } finally {
            setShowLoading(false);
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
            setHeader(reader.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        multiple: false,
    });

    // Function to edit the template
    const updateTemplate = async () => {
        let info = {};

        let get_messages = viewWhatsAppScreenProps.messages.messages.map((message) => {
            return {
                type: (message.type === "whatsapp" || message.type === "sms") ? "text" : message.type === "img" ? "img" : message.type,
                content: message.message
            };
        });

        if (headerType === "text" || headerType === "image" && header !== "") {
            let headerObject = { type: headerType === "image" ? "header-img" : "header-txt", content: header }

            get_messages = [
                ...get_messages,
                headerObject
            ];
        }

        info = {
            title: title,
            messages: get_messages,
            msg_type: selected !== 'Em análise' ? selected : 'Em análise',
            channel_type: 'WhatsAppAPI',
            id_template: templateId
        };

        console.log(info);

        const resultJson = await TemplateUpdate(info);

        if (resultJson["status"] === "success") {
            notify("Success", "Modelo editado com sucesso!", true);
            setAlreadyFetchTemplates(false);
            Navigate("/modelos");
        }
    };

    const sendTestMessage = async () => {
        let info = {};

        const get_messages = viewWhatsAppScreenProps.messages.messages.map((message) => {
            return {
                type: (message.type === "whatsapp" || message.type === "sms") ? "text" : message.type === "img" ? "img" : message.type,
                content: message.message
            };
        });

        info = {
            title: title,
            messages: get_messages,
            msg_type: selected !== 'Em análise' ? selected : 'Em análise',
            channel_type: typeChannel,
            number: String(`55${wppNumber}`).replace(/\D/g, "")
        };


        await TestMessage(info).then((response) => {
            if (response.status === "success") {
                notify("Success", "Mensagem enviada com sucesso!", true);
            } else {
                notify("Error", "Ocorreu algum erro, tente novamente mais tarde.", true);
            }


            setOpenTestModal(false);
        })
    }

    const verifyFieldsBeforeSave = () => {
        if (title === "") {
            notify("Error", "Preencha o título do modelo", true);
        } else if ((String(typeChannel).includes("WhatsApp") && String(messageTemporary).length === 0)) {
            notify("Error", "Escreva uma mensagem antes de salvar o modelo", true);
        } else {
            setOpenModal(true);
        }
    }

    const handleChange = (event) => {
        setHeaderType(event.target.value);
        setHeader("");
    };

    // Show the loading screen
    if (showLoading) {
        return <Loading open={true} />;
    }

    return (
        <>
            <HeaderPage />
            <Box sx={{ background: "#F7F7FC" }}>
                <Grid container spacing={2} style={{ paddingTop: '20px' }}>
                    <Grid item xs={12} lg={6} xl={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ flexGrow: 1, justifyContent: 'center', backgroundColor: '#F7F7FC', width: { xs: 'calc(100% - 20px)', sm: "561.200px", lg: "90%" }, margin: { xs: '0 auto 20px', lg: '0 auto' }, padding: { xs: 0, lg: "20px 0px 20px 30px", xl: "20px" } }}>
                            <FormControl fullWidth>
                                <TextField
                                    id="outlined-basic"
                                    label="Nome do seu Modelo"
                                    variant="standard"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    InputLabelProps={{
                                        style: { fontSize: '20px' }
                                    }}

                                />
                            </FormControl>

                            {
                                selected === 'Em análise' ? (
                                    <Box item xs={12} sx={{
                                        background: themeColors.yellow01,
                                        color: themeColors.yellow03,
                                        fontSize: '14px',
                                        padding: '13px',
                                        borderRadius: '4px',
                                        textTransform: "none",
                                        width: "100%",
                                        display: "flex",
                                        gap: "10px",
                                        marginTop: "20px",
                                        alignItems: "center"
                                    }}>
                                        <StatusIcon status={"Alert"} color={themeColors.yellow03} />
                                        A categoria desse tipo de modelo será definida pela Meta.
                                    </Box>
                                ) : (
                                    <Box item xs={12} sx={{
                                        background: themeColors.green01,
                                        color: themeColors.green03,
                                        fontSize: '14px',
                                        padding: '13px',
                                        borderRadius: '4px',
                                        textTransform: "none",
                                        width: "100%",
                                        display: "flex",
                                        gap: "10px",
                                        marginTop: "20px",
                                        alignItems: "center"
                                    }}>
                                        <StatusIcon status={"Alert"} color={themeColors.green03} />
                                        <p>Esse modelo é da categoria <strong>{selected}</strong>.</p>
                                    </Box>
                                )
                            }

                            <Box sx={{ paddingTop: { xs: '20px', sm: '0px' }, marginBottom: (isSmallScreen && showPreview) ? '20px' : 'none', position: 'relative' }}>
                                <Box sx={{
                                    color: 'rgb(129, 66, 252)',
                                    fontSize: '18px',
                                    position: 'absolute',
                                    top: { xs: (typeChannel === 'WhatsApp' || typeChannel === 'SMS') ? '16px' : (typeChannel === 'E-mail' && showPreview) ? '16px' : '0px', sm: (typeChannel === 'WhatsApp' || typeChannel === 'SMS') ? '16px' : (typeChannel === 'E-mail' && showPreview) ? '16px' : '0px' },
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    display: isSmallScreen ? 'block' : 'none',
                                    fontFamily: 'Satoshi',
                                    cursor: 'pointer',
                                    zIndex: '10'
                                }} onClick={() => setShowPreview(prev => !prev)}>
                                    {
                                        !showPreview ? "Exibir Prévia" : "Esconder Prévia"
                                    }
                                </Box>

                                <Box sx={{
                                    display: (isSmallScreen && showPreview) ? 'none' : 'block'
                                }}>
                                    <Grid container spacing={2} style={{ marginTop: '20px' }}>
                                        <FormControl component="fieldset" fullWidth sx={{ paddingLeft: "16px" }}>
                                            <FormLabel component="legend" sx={{
                                                color: '#000',
                                                '&.MuiFormLabel-root.Mui-focused': {
                                                    color: '#000',
                                                },
                                                paddingBottom: "16px"
                                            }}>Cabeçalho</FormLabel>
                                            <RadioGroup row value={headerType} onChange={handleChange}>
                                                <FormControlLabel value="none" control={<Radio />} label="Nenhum" />
                                                <FormControlLabel value="text" control={<Radio />} label="Texto" />
                                                <FormControlLabel value="image" control={<Radio />} label="Imagem" />
                                            </RadioGroup>
                                        </FormControl>

                                        {
                                            headerType === "text" ? (
                                                <TemplateInput type={'whatsappapi'} value={header} handleChange={setHeader} variablesOfTemplate={variablesOfTemplate} multiline={false} header={true} />
                                            ) : headerType === "image" ? (
                                                <Box sx={{ position: 'relative', width: '100%' }}>
                                                    <Box
                                                        {...getRootProps()}
                                                        sx={{
                                                            width: 'calc(100% - 16px)',
                                                            height: 56,
                                                            border: '2px dashed #ccc',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            backgroundColor: isDragActive ? '#f0f0f0' : 'transparent',
                                                            marginTop: '16px',
                                                            marginLeft: '16px',
                                                        }}
                                                    >
                                                        <input {...getInputProps()} />
                                                        <Typography variant="body1" color="textSecondary">
                                                            Clique ou arraste sua foto aqui
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : null
                                        }

                                        <TemplateInput type={'whatsappapi'} value={messageTemporary} handleChange={setMessageTemporary} variablesOfTemplate={variablesOfTemplate} />

                                        <Grid item xs={6}>
                                            <FormControl fullWidth sx={{ justifyContent: 'flex-end' }}>
                                                {disabledTestModal ? (
                                                    <Tooltip arrow title="Após a edição do modelo da WhatsAppAPI, é necessário salvá-lo e aguardar a análise antes de enviá-lo ou testá-lo.">
                                                        <span>
                                                            <Button
                                                                disabled
                                                                onClick={() => setOpenTestModal(true)}
                                                                sx={{
                                                                    width: '100%',
                                                                    padding: '13px',
                                                                    background: themeColors.purple300,
                                                                    '&:hover': { background: themeColors.purple400 },
                                                                    color: themeColors.purple700,
                                                                }}
                                                                variant="contained"
                                                            >
                                                                Testar Envio
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Button
                                                        onClick={() => setOpenTestModal(true)}
                                                        sx={{
                                                            width: '100%',
                                                            padding: '13px',
                                                            background: themeColors.purple300,
                                                            '&:hover': { background: themeColors.purple400 },
                                                            color: themeColors.purple700,
                                                        }}
                                                        variant="contained"
                                                    >
                                                        Testar Envio
                                                    </Button>
                                                )}
                                            </FormControl>

                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControl fullWidth sx={{ justifyContent: 'flex-end' }}>
                                                <DynamicButton handleFunction={verifyFieldsBeforeSave} style={{ width: '100%', padding: '13px' }} variant={"contained"} text={"Editar Modelo"} />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>

                            <Box sx={{ display: { xs: 'none', lg: 'flex' }, justifyContent: 'start', alignItems: 'end', minHeight: '80px' }}>
                                <Footer />
                            </Box>
                        </Box>
                    </Grid>
                    {
                        (isSmallScreen && showPreview === true) ? (
                            <Grid item xs={12} lg={6} xl={6} padding={{ xs: "0 0 20px 0", lg: "0", xl: "0" }}>
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFF', padding: '20px', borderRadius: '15px', minHeight: { xs: "696px", lg: "100%", xl: "100%" }, width: { xs: "100%", sm: "561.200px", lg: "100%", xl: "100%" }, margin: { xs: "0 auto", lg: "0", xl: "0" }, borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px' }}>
                                    <ViewWhatsAppScreen {...viewWhatsAppScreenProps} header={{ type: headerType, content: header }} />
                                </Box>
                            </Grid>
                        ) : (!isSmallScreen) ? (
                            <Grid item xs={12} lg={6} xl={6} padding={{ xs: "0 0 20px 0", lg: "0", xl: "0" }}>
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFF', padding: '20px', borderRadius: '15px', minHeight: { xs: "696px", lg: "100%", xl: "100%" }, width: { xs: "561.200px", lg: "100%", xl: "100%" }, margin: { xs: "0 auto", lg: "0", xl: "0" }, borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px' }}>
                                    <ViewWhatsAppScreen {...viewWhatsAppScreenProps} header={{ type: headerType, content: header }} />
                                </Box>
                            </Grid>
                        ) : null
                    }
                    <Box sx={{ padding: { xs: "20px 0 0 25px", sm: "20px 0 0 0" }, display: { xs: "block", lg: "none" }, width: { xs: "100%", sm: "561.200px", lg: "100%", xl: "100%" }, margin: { sm: "0 auto", lg: "0", xl: "0" } }}>
                        <Footer />
                    </Box>
                </Grid>

            </Box>
            <TestTemplateModal isOpen={openTestModal} onClose={() => setOpenTestModal(false)} onConfirm={sendTestMessage} wppNumber={wppNumber} setWppNumber={setWppNumber} channelType={"WhatsAppAPI"} />
            <ModalR
                cancelText={"Mudei de ideia"}
                color={ThemeColors.purple500}
                confirmText={"Salvar alterações"}
                subTitle={"Deseja salvar as alterações feitas no modelo?"}
                isOpen={openModal}
                onClose={() => setOpenModal(false)}
                onConfirm={async () => await updateTemplate()}
                title={"Editar Modelo"}
                emoji={"emoji01"}
                iconColor={ThemeColors.purple400}
            />
        </>
    );
}

export default EditWhatsAppAPI;
